import { CSSProperties, FC, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Card, FlexContent } from "@rothoblaas/design-system";
import { resolveAccentColor } from "../../../constants/colors";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { CalculationEntry } from "../../../http/concreteResin/types/calculationEntries";
import { ConcreteResinCalculation, ConcreteResinFormValues } from "../../../http/concreteResin/types/calculations";
import {
  buildResinFormDefaults,
  useAutoSaveResinCalculation,
} from "../../../http/concreteResin/useAutoSaveResinCalculation";
import { useCalculationEntries } from "../../../http/concreteResin/useCalculationEntries";
import { useConcreteResinSettings } from "../../../http/concreteResin/useSettings";
import { Calculation } from "../../../http/types/calculations";
import CalculationEntriesList from "./CalculationEntriesList";
import styles from "./ConcreteResin.module.css";
import ConcreteResinDetailInstructionsAndNotes from "./ConcreteResinDetailInstructionsAndNotes";
import ConcreteResinForm from "./ConcreteResinForm";
import ConcreteResinPdfExportButton from "./ConcreteResinPdfExportButton";
import ConcreteResinPreview from "./ConcreteResinPreview";
import ConcreteResinSummary from "./ConcreteResinSummary";
import ConcreteResinUnitToggle from "./ConcreteResinUnitToggle";
import SaveCalculationEntryButton from "./SaveCalculationEntryButton";

/**
 * GRID FOR RESIN QUANTITY CALCULATION
 */
export type ResinQuantityProps = {
  data?: Calculation<ConcreteResinCalculation>;
  isAdmin?: boolean;
  /** When true, all editable form fields are disabled */
  readOnly?: boolean;
  /** Where the page header's buttons go, e.g. the unit system toggle (see Calculation.tsx) */
  headerButtonsContainer?: HTMLElement | null;
};

// Renders on a save's response (data) or a loaded entry only: the cards are memoized with stable props,
// and each watches the form values it shows itself, so typing re-renders no page.
const ResinQuantityCalculation: FC<ResinQuantityProps> = ({
  data,
  isAdmin,
  readOnly = false,
  headerButtonsContainer,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  // The calculation's values when the page is mounted: data changes on every save too, which must not
  // override what is being typed.
  const [defaultValues] = useState<ConcreteResinFormValues>(() => buildResinFormDefaults(data));
  const formMethods = useForm<ConcreteResinFormValues>({
    defaultValues,
    mode: "onChange",
  });

  const handleAutoSaveError = useCallback(
    (err: unknown) => dispatch(showErrorNotification(i18n, err)),
    [dispatch, i18n],
  );
  const { triggerSave, waitForSave } = useAutoSaveResinCalculation(data, formMethods, isAdmin, handleAutoSaveError);

  // Nothing can be picked or computed without the calculation's settings (one request, shared with the
  // fields): every input and action waits for them.
  const { data: settings, error: settingsError } = useConcreteResinSettings(data?.id);
  const isLocked = readOnly || !settings;
  useEffect(() => {
    if (settingsError) dispatch(showErrorNotification(i18n, settingsError));
  }, [dispatch, i18n, settingsError]);

  const entries = useCalculationEntries(data?.id, data?.data?.entries);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  // A change of the form after loading an entry clears its highlight. Subscribed outside render, as
  // reading formState here would re-render the page.
  useEffect(
    () =>
      formMethods.subscribe({
        formState: { isDirty: true },
        callback: ({ isDirty }) => {
          if (isDirty) setSelectedEntryId(null);
        },
      }),
    [formMethods],
  );

  const handleLoadEntry = useCallback(
    (entry: CalculationEntry) => {
      formMethods.reset({
        // a saved entry holds measurements only: keep showing them in the chosen unit system
        unitSystem: formMethods.getValues("unitSystem"),
        chemicalAnchorId: entry.chemicalAnchorId,
        rodDiameter: entry.rodDiameter,
        holeDiameter: entry.holeDiameter,
        holeDepth: entry.holeDepth,
        holeNumbers: entry.numHoles,
        overageFactor: entry.overageFactor,
      });
      setSelectedEntryId(entry.id);
      triggerSave();
    },
    [formMethods, triggerSave],
  );

  const resinTranslation = i18n.translation.concreteResinModule;
  const accentColor = resolveAccentColor(data?.module?.category?.color);

  return (
    <FormProvider {...formMethods}>
      {/* Portaled into the page header, but still rendered here so it stays inside FormProvider. */}
      {headerButtonsContainer &&
        createPortal(<ConcreteResinUnitToggle accentColor={accentColor} disabled={isLocked} />, headerButtonsContainer)}
      <FlexContent padding noPaddingTop>
        <div className={styles.grid} style={{ "--accent-base": accentColor } as CSSProperties}>
          <Card
            style={{ minHeight: 400 }}
            accentColor="var(--accent-base, var(--red))"
            shadow
            title={resinTranslation.titleHoleGeometry}
          >
            <ConcreteResinForm calculationId={data?.id} readOnly={isLocked} />
            <div className={styles.formFooter}>
              {/* explains the overage factor, the form's last field */}
              <p className={styles.formNote}>{resinTranslation.instruction2}</p>
              {!readOnly && !!data?.id && (
                <SaveCalculationEntryButton calculationId={data.id} waitForSave={waitForSave} disabled={!settings} />
              )}
            </div>
          </Card>

          {/* The plain cards keep the design-system's thin top border; their class makes up for the
              accented cards' thicker one, so their content lines up with theirs. */}
          <Card
            className={styles.previewCard}
            style={{ minHeight: 400 }}
            shadow
            title={i18n.translation.common.titlePreview}
          >
            <ConcreteResinPreview />
          </Card>

          <Card
            style={{ minHeight: 400 }}
            accentColor="var(--accent-base, var(--red))"
            shadow
            title={resinTranslation.titleSummary}
          >
            {/* the results of the last save, the server's */}
            <ConcreteResinSummary data={data?.data ?? null} />
          </Card>

          <Card
            className={styles.plainCard}
            style={{ minHeight: 400 }}
            shadow
            title={resinTranslation.titleInstructionsAndNotes}
          >
            <ConcreteResinDetailInstructionsAndNotes />
          </Card>

          {/* Its header is composed here, as the title prop only takes the heading: the PDF button
              goes on its right. */}
          <Card
            className={styles.savedCalculationsCard}
            style={{ minHeight: 400 }}
            accentColor="var(--accent-base, var(--red))"
            shadow
          >
            <div className={styles.cardHeader}>
              <Card.Title>{resinTranslation.titleCalculationsSaved}</Card.Title>
              {!!data?.id && (
                <ConcreteResinPdfExportButton
                  calculationId={data.id}
                  hasEntries={!!entries && entries.length > 0}
                  disabled={!settings}
                  moduleName={data.module?.name}
                  calculationName={data.name}
                  projectName={data.project?.name}
                />
              )}
            </div>
            <div className={styles.entriesRow}>
              <CalculationEntriesList
                calculationId={data?.id ?? ""}
                entries={entries}
                selectedEntryId={selectedEntryId}
                onLoadEntry={handleLoadEntry}
                readOnly={isLocked}
              />
            </div>
          </Card>
        </div>
      </FlexContent>
    </FormProvider>
  );
};

// Mounted again for another calculation: the form starts from its values, its fields' effects
// (auto-selecting a single option) run on them, and no save of the previous one is left pending.
const ResinQuantity: FC<ResinQuantityProps> = (props) => <ResinQuantityCalculation key={props.data?.id} {...props} />;

export default ResinQuantity;
